import { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Button } from "@stringf/ui/button";
import { Lost } from "@/components/sheet/Lost";

/**
 * Route-level error screen. Mounted as `errorElement` on the routes *inside*
 * the `/y` layout so a crash renders in the Outlet and the nav shell survives —
 * the user can still walk to another room instead of losing the whole app.
 *
 * The top-level Sentry `ErrorBoundary` never sees these: React Router catches
 * them first, so this component reports them itself.
 *
 * Retry is a document reload, not an in-place reset. React Router has no API to
 * clear a route's error state when the route has no loader, and a reload is the
 * right recovery for the failure mode code splitting introduces anyway: a
 * client running against a deploy whose chunks no longer exist needs fresh
 * `index.html`. The nav shell being preserved comes from where this is mounted,
 * not from how retry works.
 *
 * `world="sheet"` is the same screen for a public route (the landing, the auth
 * flows, About, FAQ): there is no shell to keep, so it is a whole page in the
 * One Sheet world, with the retry as its plate (DESIGN.md §10). Home is a plain
 * `<a>`, a full load, because the router is what failed.
 */
export function RouteError({ world = "app" }: { world?: "app" | "sheet" }) {
  const error = useRouteError();
  const { t } = useTranslation();

  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: "route" } });
  }, [error]);

  if (world === "sheet") {
    return (
      <Lost
        mark={
          <a href="/" className="sh-wordmark">
            aSTRING
          </a>
        }
        title={t("errors.route.title")}
        body={t("errors.route.body")}
      >
        <button type="button" className="sh-plate" onClick={() => window.location.reload()}>
          {t("errors.route.retry")}
        </button>
        <a href="/" className="sh-textlink">
          {t("errors.notFound.home")}
        </a>
      </Lost>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <h2 className="text-subhead text-text-1">{t("errors.route.title")}</h2>
      <p className="text-body text-text-2 max-w-sm">{t("errors.route.body")}</p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.reload()}
      >
        {t("errors.route.retry")}
      </Button>
    </div>
  );
}
